import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const pixelGrid: TemplateRegistration = {
  meta: {
    id: 'pixel-grid',
    name: 'Pixel Grid',
    category: 'Creative',
    description: 'Pixel-art inspired with blocky grid sections',
    previewImage: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Press Start 2P', 'Lexend']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-light">
        <div class="container">
          <h2 class="section-title">&gt; About_Us</h2>
          <div class="about-card">
            <p class="about-text">${safeAbout}</p>
            <div class="about-stats">
              ${rating ? `<div class="stat-box"><span class="stat-value">${rating.toFixed(1)}</span><span class="stat-label">Rating</span></div>` : ''}
              ${reviewCount ? `<div class="stat-box"><span class="stat-value">${reviewCount}</span><span class="stat-label">Reviews</span></div>` : ''}
              ${data.services.length > 0 ? `<div class="stat-box"><span class="stat-value">${data.services.length}</span><span class="stat-label">Services</span></div>` : ''}
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-block">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">&gt; Services</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url) => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-light">
        <div class="container">
          <h2 class="section-title">&gt; Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map((t) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-block">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">&gt; Reviews</h2>
          <div class="testimonials-stack">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-light">
        <div class="container">
          <h2 class="section-title">&gt; FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">&gt; Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-light">
        <div class="container">
          <h2 class="section-title">&gt; Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">&gt; Contact</h2>
          <div class="contact-full">
            <div class="contact-info-row">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">SEND_MSG</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #06B6D4; --bg: #111827; --bg-alt: #1F2937; --accent: #D946EF;
      --text: #F9FAFB; --text-muted: #9CA3AF;
      --font-heading: 'Press Start 2P', monospace; --font-body: 'Lexend', sans-serif;
      --shadow-sm: 4px 4px 0 rgba(6,182,212,.2); --shadow-md: 4px 4px 0 var(--primary);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--accent); }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(17,24,39,.95); backdrop-filter: blur(10px); border-bottom: 4px solid var(--primary); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: .7rem; color: var(--primary); letter-spacing: 1px; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .8rem; font-weight: 500; letter-spacing: .5px; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; }

    /* HERO - outlined text behind, solid text in front */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: var(--bg); }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .1; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(17,24,39,.8), var(--bg)); }
    .hero-content { position: relative; z-index: 2; padding: 120px 24px 80px; max-width: 1200px; margin: 0 auto; width: 100%; }
    .hero-text-layer { position: relative; margin-bottom: 24px; }
    .hero-outline-text { font-family: var(--font-heading); font-size: clamp(3rem, 10vw, 7rem); line-height: 1; color: transparent; -webkit-text-stroke: 2px var(--primary); text-shadow: none; opacity: .25; position: absolute; top: -20px; left: -8px; white-space: nowrap; pointer-events: none; text-transform: uppercase; letter-spacing: 2px; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(.9rem, 3vw, 1.6rem); line-height: 2; color: var(--primary); letter-spacing: 1px; position: relative; z-index: 1; }
    .hero h1 .accent { color: var(--accent); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.1rem); color: var(--text-muted); max-width: 560px; margin-bottom: 32px; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--primary); color: var(--bg); font-family: var(--font-heading); font-size: .6rem; font-weight: 400; text-transform: uppercase; letter-spacing: 2px; border: 4px solid var(--primary); cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: var(--shadow-md); }
    .hero-cta:hover { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 var(--primary); color: var(--bg); }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-light { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(.7rem, 2vw, 1rem); line-height: 1.8; margin-bottom: 40px; color: var(--primary); letter-spacing: 1px; }

    /* ABOUT - card with stat highlight boxes */
    .about-card { background: var(--bg-alt); border: 4px solid var(--primary); box-shadow: var(--shadow-md); padding: 36px 32px; display: flex; gap: 32px; align-items: flex-start; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); max-width: 600px; line-height: 1.9; flex: 1; }
    .about-stats { display: flex; flex-direction: column; gap: 16px; flex-shrink: 0; }
    .stat-box { background: var(--primary); padding: 16px 20px; text-align: center; box-shadow: var(--shadow-sm); }
    .stat-box .stat-value { font-family: var(--font-heading); font-size: .7rem; line-height: 1.6; color: var(--accent); display: block; }
    .stat-box .stat-label { font-size: .7rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: .5px; }

    /* SERVICES - 2-col pixel/tech cards */
    .services-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .service-block { padding: 28px 24px; background: var(--bg); border: 4px solid var(--primary); box-shadow: var(--shadow-md); transition: transform .2s, box-shadow .2s; position: relative; overflow: hidden; }
    .service-block::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 4px; background: repeating-linear-gradient(90deg, var(--primary) 0, var(--primary) 8px, transparent 8px, transparent 16px); }
    .service-block:hover { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 var(--accent); }
    .service-icon { margin-bottom: 12px; }
    .icon-char { font-size: 24px; line-height: 1; color: var(--accent); }
    .service-block h3 { font-family: var(--font-heading); font-size: .55rem; line-height: 1.8; margin-bottom: 10px; color: var(--text); letter-spacing: .5px; }
    .service-block p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; font-weight: 700; font-size: 1.05rem; color: var(--accent); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border: 4px solid var(--primary); transition: border-color .2s; }
    .gallery-item img:hover { border-color: var(--accent); }

    /* TESTIMONIALS - stacked alternating left/right */
    .testimonials-stack { display: flex; flex-direction: column; gap: 24px; max-width: 800px; margin: 0 auto; }
    .testimonial-block { padding: 28px; background: var(--bg); border: 4px solid var(--accent); box-shadow: 4px 4px 0 var(--accent); transition: transform .2s; max-width: 600px; }
    .testimonial-block:nth-child(odd) { align-self: flex-start; }
    .testimonial-block:nth-child(even) { align-self: flex-end; }
    .testimonial-block:hover { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 var(--accent); }
    .testimonial-body p { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #374151; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; }
    .faq-item { border-bottom: 4px solid var(--bg-alt); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: .55rem; line-height: 1.8; margin-bottom: 10px; color: var(--primary); letter-spacing: .5px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 4px solid var(--bg-alt); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: .5rem; line-height: 1.8; margin-bottom: 6px; color: var(--text); letter-spacing: .5px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-body); font-size: 1.4rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 4px solid var(--bg-alt); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--primary); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-alt); padding: 20px 0; border-bottom: 4px solid var(--accent); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: #fff; font-family: var(--font-heading); padding: 8px 16px; font-size: .5rem; line-height: 1.6; letter-spacing: .5px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - full-width form */
    .contact-full { max-width: 700px; margin: 0 auto; }
    .contact-info-row { display: flex; gap: 24px; margin-bottom: 24px; flex-wrap: wrap; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; font-size: .85rem; color: var(--primary); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 4px solid var(--primary); color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; width: 100%; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 14px; background: var(--accent); color: #fff; font-family: var(--font-heading); font-size: .5rem; line-height: 1.6; letter-spacing: 1px; text-transform: uppercase; border: 4px solid var(--accent); cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: 4px 4px 0 rgba(217,70,239,.4); }
    .contact-form button:hover { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 rgba(217,70,239,.5); }

    /* FOOTER */
    .footer { background: #030712; padding: 40px 0; text-align: center; border-top: 4px solid var(--primary); }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(17,24,39,.98); border-bottom: 4px solid var(--primary); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .about-card { flex-direction: column; }
      .about-stats { flex-direction: row; }
      .testimonial-block { max-width: 100%; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <div class="hero-text-layer">
        <span class="hero-outline-text">${safeHeadline}</span>
        <h1>${safeHeadline}</h1>
      </div>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">START_GAME</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
